// Small hand-written SVG charts: score histogram, sparkline, difficulty curve,
// option pick-rate bars. Thin marks, 2px gaps, recessive hairline grid, hover
// tooltips; text uses text tokens, never the data colour.

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import styles from "./charts.module.css";

function useWidth<T extends HTMLElement>(fallback = 640) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof ResizeObserver === "undefined") {
      setWidth(node.clientWidth || fallback);
      return;
    }
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.max(120, Math.floor(entry.contentRect.width)));
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [fallback]);
  return [ref, width] as const;
}

const fmt = new Intl.NumberFormat("en-US");

// ---------------------------------------------------------------- tooltip

function Tooltip({ x, y, children, width }: { x: number; y: number; children: ReactNode; width: number }) {
  const left = Math.min(Math.max(x, 70), width - 70);
  return (
    <div className={styles.tooltip} style={{ left, top: y }} role="status">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------- histogram

/** 50 bins over score 0–1000 (bin i = [20i, 20i+20)). Bin 0 is mostly wrong answers (score 0). */
export function ScoreHistogram({ bins, height = 170 }: { bins: number[]; height?: number }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const padTop = 18;
  const padBottom = 24;
  const padL = 30;
  const plotW = Math.max(60, width - padL);
  const plotH = height - padTop - padBottom;
  // Bin 0 holds every wrong answer (score 0) and can dwarf the rest; clip it with a marked break.
  const restMax = Math.max(1, ...bins.slice(1));
  const clipZero = (bins[0] ?? 0) > restMax * 1.6;
  const max = clipZero ? restMax * 1.15 : Math.max(1, ...bins);
  const band = plotW / Math.max(1, bins.length);
  const barW = Math.max(1, Math.min(24, band - 2));
  const total = bins.reduce((a, b) => a + b, 0);
  // Integer y ticks only, without repeats (small days have maxima like 1 or 2).
  const yTicks = [...new Set([Math.floor(max / 2), Math.floor(max)])].filter((v) => v >= 1);

  return (
    <div ref={ref} className={styles.chart} onMouseLeave={() => setHover(null)}>
      <svg width={width} height={height} role="img" aria-label={`Score distribution of ${fmt.format(total)} submissions in ${bins.length} bins`}>
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={padL} x2={width} y1={padTop + plotH * (1 - v / max)} y2={padTop + plotH * (1 - v / max)} className={styles.grid} />
            <text x={padL - 8} y={padTop + plotH * (1 - v / max) + 3} className={styles.tick} textAnchor="end">
              {fmt.format(v)}
            </text>
          </g>
        ))}
        <line x1={padL} x2={width} y1={padTop + plotH} y2={padTop + plotH} className={styles.axis} />
        {bins.map((v, i) => {
          const h = v === 0 ? 0 : Math.min(plotH, Math.max(2, (v / max) * plotH));
          const x = padL + i * band + (band - barW) / 2;
          const y = padTop + plotH - h;
          const r = Math.min(2, barW / 2, h / 2);
          return (
            <g key={i}>
              <path
                d={`M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + barW - r} Q${x + barW},${y} ${x + barW},${y + r} V${y + h} Z`}
                className={styles.bar}
                data-kind={i === 0 ? "zero" : undefined}
                data-hover={hover === i || undefined}
              />
              <rect x={padL + i * band} y={padTop} width={band} height={plotH} fill="transparent" onMouseEnter={() => setHover(i)} />
            </g>
          );
        })}
        {[0, 250, 500, 750, 1000].map((s) => (
          <text
            key={s}
            x={Math.min(width - 2, padL + (s / 1000) * plotW)}
            y={height - 6}
            className={styles.tick}
            textAnchor={s === 1000 ? "end" : "middle"}
          >
            {s}
          </text>
        ))}
        {clipZero ? (
          <>
            <path
              d={`M${padL + (band - barW) / 2 - 2},${padTop + 12} l${barW + 4},-5 M${padL + (band - barW) / 2 - 2},${padTop + 17} l${barW + 4},-5`}
              className={styles.break}
            />
            <text x={padL + band + 6} y={padTop + 8} className={styles.tick}>
              {fmt.format(bins[0] ?? 0)} at score 0 (wrong or gave up) · clipped
            </text>
          </>
        ) : null}
      </svg>
      {hover !== null ? (
        <Tooltip x={padL + hover * band + band / 2} y={padTop - 6} width={width}>
          <span className="mono">
            {hover * 20}–{hover * 20 + 19}
          </span>
          <strong>{fmt.format(bins[hover] ?? 0)}</strong>
          {hover === 0 ? <span className={styles.tooltipNote}>incl. wrong answers (score 0)</span> : null}
        </Tooltip>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- sparkline

export function Sparkline({
  values,
  width = 96,
  height = 26,
  label,
}: {
  values: (number | null)[];
  width?: number;
  height?: number;
  label: string;
}) {
  const nums = values.filter((v): v is number => v !== null);
  if (nums.length === 0) return <span className={styles.sparkEmpty}>—</span>;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min || 1;
  const step = values.length > 1 ? (width - 6) / (values.length - 1) : 0;
  const pts = values.map((v, i) => (v === null ? null : ([3 + i * step, 3 + (height - 6) * (1 - (v - min) / span)] as const)));
  let d = "";
  let pen = false;
  for (const p of pts) {
    if (!p) {
      pen = false;
      continue;
    }
    d += `${pen ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)} `;
    pen = true;
  }
  const last = [...pts].reverse().find(Boolean);
  return (
    <svg className={styles.spark} width={width} height={height} role="img" aria-label={label}>
      <path d={d} className={styles.sparkLine} />
      {last ? <circle cx={last[0]} cy={last[1]} r={2.5} className={styles.sparkDot} /> : null}
    </svg>
  );
}

// ---------------------------------------------------------------- difficulty curve

export interface CurvePoint {
  n: number;
  score: number;
  flags: string[];
  label?: string;
}

const FLAG_ORDER = ["gate", "relief", "intro", "chapterEnd"] as const;

function Marker({ flag, x, y }: { flag: string; x: number; y: number }) {
  if (flag === "gate") return <path d={`M${x},${y - 6} L${x + 5.5},${y + 4} L${x - 5.5},${y + 4} Z`} className={styles.mGate} />;
  if (flag === "relief") return <circle cx={x} cy={y} r={4.5} className={styles.mRelief} />;
  if (flag === "intro") return <path d={`M${x},${y - 5.5} L${x + 5.5},${y} L${x},${y + 5.5} L${x - 5.5},${y} Z`} className={styles.mIntro} />;
  return <circle cx={x} cy={y} r={5} className={styles.mChapter} />;
}

export function DifficultyCurve({
  points,
  selected,
  onSelect,
  target,
  height = 230,
}: {
  points: CurvePoint[];
  selected?: number | null;
  onSelect?: (n: number) => void;
  target?: (n: number) => number;
  height?: number;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  if (points.length === 0) return null;
  const padL = 34;
  const padR = 12;
  const padT = 14;
  const padB = 26;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;
  const first = points[0]!.n;
  const last = points[points.length - 1]!.n;
  const span = Math.max(1, last - first);
  const x = (n: number) => padL + ((n - first) / span) * plotW;
  const y = (s: number) => padT + (1 - Math.min(1, Math.max(0, s))) * plotH;
  const line = points.map((p, i) => `${i ? "L" : "M"}${x(p.n).toFixed(1)},${y(p.score).toFixed(1)}`).join(" ");
  const targetLine = target
    ? points.map((p, i) => `${i ? "L" : "M"}${x(p.n).toFixed(1)},${y(target(p.n)).toFixed(1)}`).join(" ")
    : null;
  const band = plotW / Math.max(1, points.length - 1);
  const hovered = hover !== null ? points.find((p) => p.n === hover) : null;
  const sel = selected !== null && selected !== undefined ? points.find((p) => p.n === selected) : null;

  const onKey = (e: KeyboardEvent) => {
    if (!onSelect) return;
    const cur = selected ?? first - 1;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      onSelect(Math.min(last, cur + 1));
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      onSelect(Math.max(first, cur - 1));
    }
  };

  return (
    <div ref={ref} className={styles.chart} onMouseLeave={() => setHover(null)}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={`Difficulty curve for levels ${first} to ${last}. Use left and right arrows to move between levels.`}
        tabIndex={onSelect ? 0 : undefined}
        onKeyDown={onKey}
        className={styles.focusable}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <g key={t}>
            <line x1={padL} x2={width - padR} y1={y(t)} y2={y(t)} className={t === 0 ? styles.axis : styles.grid} />
            <text x={padL - 8} y={y(t) + 3} className={styles.tick} textAnchor="end">
              {t.toFixed(2).replace(/^0/, "")}
            </text>
          </g>
        ))}
        {points
          .filter((p) => p.n % 10 === 0 || p.n === first)
          .map((p) => (
            <text key={p.n} x={x(p.n)} y={height - 7} className={styles.tick} textAnchor="middle">
              {p.n}
            </text>
          ))}
        {targetLine ? <path d={targetLine} className={styles.target} /> : null}
        <path d={line} className={styles.curve} />
        {points.map((p) => {
          const flag = FLAG_ORDER.find((f) => p.flags.includes(f));
          return flag ? <Marker key={p.n} flag={flag} x={x(p.n)} y={y(p.score)} /> : <circle key={p.n} cx={x(p.n)} cy={y(p.score)} r={2} className={styles.point} />;
        })}
        {sel ? <circle cx={x(sel.n)} cy={y(sel.score)} r={8} className={styles.selected} /> : null}
        {hovered ? <line x1={x(hovered.n)} x2={x(hovered.n)} y1={padT} y2={padT + plotH} className={styles.crosshair} /> : null}
        {points.map((p) => (
          <rect
            key={`hit-${p.n}`}
            x={x(p.n) - band / 2}
            y={padT}
            width={band}
            height={plotH}
            fill="transparent"
            style={{ cursor: onSelect ? "pointer" : undefined }}
            onMouseEnter={() => setHover(p.n)}
            onClick={() => onSelect?.(p.n)}
          />
        ))}
      </svg>
      {hovered ? (
        <Tooltip x={x(hovered.n)} y={Math.max(4, y(hovered.score) - 46)} width={width}>
          <span className="mono">L{hovered.n}</span>
          <strong>{hovered.score.toFixed(2)}</strong>
          {hovered.flags.length ? <span className={styles.tooltipNote}>{hovered.flags.join(" · ")}</span> : null}
          {hovered.label ? <span className={styles.tooltipNote}>{hovered.label}</span> : null}
        </Tooltip>
      ) : null}
      <div className={styles.legend}>
        <span>
          <svg width={18} height={8} aria-hidden>
            <line x1={0} x2={18} y1={4} y2={4} className={styles.curve} />
          </svg>
          difficulty.score
        </span>
        {target ? (
          <span>
            <svg width={18} height={8} aria-hidden>
              <line x1={0} x2={18} y1={4} y2={4} className={styles.target} />
            </svg>
            target (docs/levels.md)
          </span>
        ) : null}
        {FLAG_ORDER.map((f) => (
          <span key={f}>
            <svg width={14} height={14} viewBox="-7 -7 14 14" aria-hidden>
              <Marker flag={f} x={0} y={0} />
            </svg>
            {f}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- pick-rate bars

export function PickRateBars({ rates, correctId }: { rates: Record<string, number>; correctId?: string | null }) {
  const ids = Object.keys(rates).sort();
  if (ids.length === 0) return null;
  return (
    <div className={styles.picks} role="list" aria-label="Option pick rates">
      {ids.map((id) => {
        const v = rates[id] ?? 0;
        const correct = id === correctId;
        return (
          <div key={id} className={styles.pickRow} role="listitem" data-correct={correct || undefined}>
            <span className={styles.pickId}>{id}</span>
            <span className={styles.pickTrack}>
              <span className={styles.pickFill} style={{ width: `${Math.max(0.5, v * 100)}%` }} />
            </span>
            <span className={styles.pickValue}>{(v * 100).toFixed(1)}%</span>
            <span className={styles.pickNote}>{correct ? "✓ correct" : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- categorical bars (monetisation)

export interface BarDatum {
  key: string;
  label: string;
  value: number;
  /** A second, fainter segment stacked after the value (e.g. impressions capped by the weekly limit). */
  extra?: number | null;
  /** Extra line in the tooltip. */
  note?: string;
}

/**
 * Horizontal bars for categories: label column, one thin bar per row, the value
 * at the end. A visually hidden table carries the same numbers for screen readers.
 */
/** A round axis maximum whose quarters are round too (counts: whole numbers). */
export function niceMax(v: number, integer = true): number {
  if (!(v > 0)) return integer ? 4 : 1;
  if (integer && v <= 40) return Math.max(4, Math.ceil(v / 4) * 4);
  const e = 10 ** Math.floor(Math.log10(v));
  const step = [1, 1.2, 1.6, 2, 3, 4, 6, 8, 10].find((s) => v <= s * e + 1e-12) ?? 10;
  return step * e;
}

export function BarChart({
  rows,
  format = (v) => fmt.format(v),
  label,
  valueName = "value",
  extraName,
  tone = "violet",
  labelWidth = 104,
  integerTicks = true,
}: {
  rows: BarDatum[];
  format?: (v: number) => string;
  label: string;
  valueName?: string;
  extraName?: string;
  tone?: "violet" | "accent" | "success" | "neutral";
  labelWidth?: number;
  /** Counts: axis ticks on whole numbers. Turn off for rates. */
  integerTicks?: boolean;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const rowH = 30;
  const barH = 12;
  const valueW = 76;
  const padT = 4;
  const height = padT + rows.length * rowH;
  const plotW = Math.max(40, width - labelWidth - valueW - 8);
  const max = niceMax(Math.max(0, ...rows.map((r) => r.value + (r.extra ?? 0))), integerTicks);
  const x0 = labelWidth;
  const quarter = [0.25, 0.5, 0.75, 1];
  return (
    <div ref={ref} className={styles.chart} onMouseLeave={() => setHover(null)} data-testid="bar-chart">
      <svg width={width} height={height + 18} role="img" aria-label={label}>
        {quarter.map((q) => (
          <line key={q} x1={x0 + q * plotW} x2={x0 + q * plotW} y1={padT} y2={height} className={styles.grid} />
        ))}
        <line x1={x0} x2={x0} y1={padT} y2={height} className={styles.axis} />
        {rows.map((r, i) => {
          const y = padT + i * rowH + (rowH - barH) / 2;
          const w = Math.max(r.value > 0 ? 2 : 0, (r.value / max) * plotW);
          const ew = r.extra ? Math.max(1, (r.extra / max) * plotW) : 0;
          return (
            <g key={r.key} onMouseEnter={() => setHover(i)} data-row={r.key}>
              <text x={0} y={y + barH - 2} className={styles.barLabel}>
                {r.label}
              </text>
              <rect x={x0} y={y} width={w} height={barH} rx={Math.min(3, w / 2)} className={styles.hbar} data-tone={tone} data-hover={hover === i || undefined} />
              {ew ? (
                <rect x={x0 + w + 2} y={y} width={Math.max(0, ew - 2)} height={barH} rx={Math.min(3, ew / 2)} className={styles.hbarExtra} data-tone={tone} />
              ) : null}
              <text x={width} y={y + barH - 2} className={styles.barValue} textAnchor="end">
                {format(r.value)}
              </text>
              <rect x={0} y={padT + i * rowH} width={width} height={rowH} fill="transparent" />
            </g>
          );
        })}
        {[0, ...quarter].map((q) => (
          <text key={q} x={x0 + q * plotW} y={height + 14} className={styles.tick} textAnchor={q === 0 ? "start" : q === 1 ? "end" : "middle"}>
            {format(q * max)}
          </text>
        ))}
      </svg>
      {hover !== null && rows[hover] ? (
        <Tooltip x={x0 + Math.min(plotW, ((rows[hover]!.value + (rows[hover]!.extra ?? 0)) / max) * plotW) / 2} y={padT + hover * rowH - 2} width={width}>
          <span>{rows[hover]!.label}</span>
          <strong>
            {format(rows[hover]!.value)} {valueName}
          </strong>
          {rows[hover]!.extra ? (
            <span className={styles.tooltipNote}>
              +{fmt.format(rows[hover]!.extra!)} {extraName}
            </span>
          ) : null}
          {rows[hover]!.note ? <span className={styles.tooltipNote}>{rows[hover]!.note}</span> : null}
        </Tooltip>
      ) : null}
      <table className="visually-hidden">
        <caption>{label}</caption>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <th scope="row">{r.label}</th>
              <td>{format(r.value)}</td>
              {extraName ? <td>{r.extra ?? "—"}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- daily columns (ads)

export interface ColumnDatum {
  key: string;
  /** Axis label (e.g. "Sep 26"); every few columns are labelled when they are dense. */
  label: string;
  value: number;
  /** Extra lines in the tooltip. */
  notes?: string[];
}

/**
 * One series over days: thin columns with 4px rounded tops on the baseline, 2px gaps, a recessive
 * grid, a per-column hover tooltip, and a visually hidden table with the same numbers.
 */
export function ColumnChart({
  rows,
  label,
  valueName = "value",
  height = 180,
  format = (v) => fmt.format(v),
  integerTicks = true,
}: {
  rows: ColumnDatum[];
  label: string;
  valueName?: string;
  height?: number;
  format?: (v: number) => string;
  /** Counts: axis ticks on whole numbers. Turn off for money and rates. */
  integerTicks?: boolean;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const axisW = 40;
  const padT = 8;
  const plotH = height - padT;
  const plotW = Math.max(40, width - axisW);
  const slot = rows.length ? plotW / rows.length : plotW;
  const barW = Math.max(2, Math.min(28, slot - 2));
  const max = niceMax(Math.max(0, ...rows.map((r) => r.value)), integerTicks);
  const every = Math.max(1, Math.ceil(rows.length / Math.max(1, Math.floor(plotW / 64))));
  const quarter = [0.25, 0.5, 0.75, 1];
  const y = (v: number) => padT + plotH - (v / max) * plotH;
  return (
    <div ref={ref} className={styles.chart} onMouseLeave={() => setHover(null)} data-testid="column-chart">
      <svg width={width} height={height + 20} role="img" aria-label={label}>
        {quarter.map((q) => (
          <g key={q}>
            <line x1={axisW} x2={width} y1={y(q * max)} y2={y(q * max)} className={styles.grid} />
            <text x={axisW - 6} y={y(q * max) + 3} className={styles.tick} textAnchor="end">
              {format(q * max)}
            </text>
          </g>
        ))}
        <line x1={axisW} x2={width} y1={padT + plotH} y2={padT + plotH} className={styles.axis} />
        {rows.map((r, i) => {
          const h = r.value > 0 ? Math.max(2, (r.value / max) * plotH) : 0;
          const x = axisW + i * slot + (slot - barW) / 2;
          const top = padT + plotH - h;
          const radius = Math.min(4, barW / 2, h);
          // Rounded top, square foot on the baseline.
          const d = h
            ? `M${x},${padT + plotH} V${top + radius} Q${x},${top} ${x + radius},${top} H${x + barW - radius} Q${x + barW},${top} ${x + barW},${top + radius} V${padT + plotH} Z`
            : "";
          return (
            <g key={r.key} onMouseEnter={() => setHover(i)} data-col={r.key}>
              <rect x={axisW + i * slot} y={padT} width={slot} height={plotH} fill="transparent" />
              {d ? <path d={d} className={styles.column} data-hover={hover === i || undefined} /> : null}
              {i % every === 0 ? (
                <text x={x + barW / 2} y={padT + plotH + 14} className={styles.tick} textAnchor="middle">
                  {r.label}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {hover !== null && rows[hover] ? (
        <Tooltip x={axisW + hover * slot + slot / 2} y={y(rows[hover]!.value) - 6} width={width}>
          <span>{rows[hover]!.label}</span>
          <strong>
            {format(rows[hover]!.value)} {valueName}
          </strong>
          {rows[hover]!.notes?.map((n) => (
            <span key={n} className={styles.tooltipNote}>
              {n}
            </span>
          ))}
        </Tooltip>
      ) : null}
      <table className="visually-hidden">
        <caption>{label}</caption>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <th scope="row">{r.label}</th>
              <td>{format(r.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- stacked daily columns (engagement)

export interface StackSeries {
  key: string;
  label: string;
  /** A colour token, e.g. var(--color-avatar-orbs-1-1); assigned per entity in a fixed order, never by rank. */
  color: string;
}

export interface StackDatum {
  key: string;
  label: string;
  values: Record<string, number>;
  /** Extra lines in the tooltip (e.g. the day's open rate). */
  notes?: string[];
}

/**
 * Several series stacked per day on one axis: segments bottom-up in series order with a 2px gap between them,
 * a 4px rounded top on the column, a legend with each series' total, a per-column tooltip listing every series,
 * and a visually hidden table with the same numbers.
 */
export function StackedColumnChart({
  rows,
  series,
  label,
  valueName = "value",
  height = 200,
  format = (v) => fmt.format(v),
}: {
  rows: StackDatum[];
  series: StackSeries[];
  label: string;
  valueName?: string;
  height?: number;
  format?: (v: number) => string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const axisW = 40;
  const padT = 8;
  const gap = 2;
  const plotH = height - padT;
  const plotW = Math.max(40, width - axisW);
  const slot = rows.length ? plotW / rows.length : plotW;
  const barW = Math.max(2, Math.min(28, slot - 2));
  const total = (r: StackDatum) => series.reduce((s, x) => s + (r.values[x.key] ?? 0), 0);
  const max = niceMax(Math.max(0, ...rows.map(total)));
  const every = Math.max(1, Math.ceil(rows.length / Math.max(1, Math.floor(plotW / 64))));
  const quarter = [0.25, 0.5, 0.75, 1];
  const y = (v: number) => padT + plotH - (v / max) * plotH;
  const sums = series.map((x) => rows.reduce((s, r) => s + (r.values[x.key] ?? 0), 0));
  return (
    <div className={styles.stackWrap}>
      <ul className={styles.legend} aria-label={`${label}: legend`}>
        {series.map((x, i) => (
          <li key={x.key}>
            <span className={styles.swatch} style={{ background: x.color }} aria-hidden />
            <span>{x.label}</span>
            <span className={styles.legendValue}>{format(sums[i]!)}</span>
          </li>
        ))}
      </ul>
      <div ref={ref} className={styles.chart} onMouseLeave={() => setHover(null)} data-testid="stacked-chart">
        <svg width={width} height={height + 20} role="img" aria-label={label}>
          {quarter.map((q) => (
            <g key={q}>
              <line x1={axisW} x2={width} y1={y(q * max)} y2={y(q * max)} className={styles.grid} />
              <text x={axisW - 6} y={y(q * max) + 3} className={styles.tick} textAnchor="end">
                {format(q * max)}
              </text>
            </g>
          ))}
          <line x1={axisW} x2={width} y1={padT + plotH} y2={padT + plotH} className={styles.axis} />
          {rows.map((r, i) => {
            const x = axisW + i * slot + (slot - barW) / 2;
            let base = padT + plotH;
            const parts = series.map((s) => ({ s, v: r.values[s.key] ?? 0 })).filter((p) => p.v > 0);
            return (
              <g key={r.key} onMouseEnter={() => setHover(i)} data-col={r.key} opacity={hover === null || hover === i ? 1 : 0.55}>
                <rect x={axisW + i * slot} y={padT} width={slot} height={plotH} fill="transparent" />
                {parts.map((p, j) => {
                  const h = Math.max(1, (p.v / max) * plotH);
                  const top = base - h;
                  const last = j === parts.length - 1;
                  // A 2px surface gap above every segment but the top one; the top one gets the rounded end.
                  const segH = last ? h : Math.max(1, h - gap);
                  const segTop = last ? top : top + gap;
                  const radius = last ? Math.min(4, barW / 2, segH) : 0;
                  const d = `M${x},${base} V${segTop + radius} Q${x},${segTop} ${x + radius},${segTop} H${x + barW - radius} Q${x + barW},${segTop} ${x + barW},${segTop + radius} V${base} Z`;
                  base = top;
                  return <path key={p.s.key} d={d} style={{ fill: p.s.color }} data-series={p.s.key} />;
                })}
                {i % every === 0 ? (
                  <text x={x + barW / 2} y={padT + plotH + 14} className={styles.tick} textAnchor="middle">
                    {r.label}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
        {hover !== null && rows[hover] ? (
          <Tooltip x={axisW + hover * slot + slot / 2} y={y(total(rows[hover]!)) - 6} width={width}>
            <span>{rows[hover]!.label}</span>
            <strong>
              {format(total(rows[hover]!))} {valueName}
            </strong>
            {series
              .filter((s) => (rows[hover]!.values[s.key] ?? 0) > 0)
              .map((s) => (
                <span key={s.key} className={styles.tooltipNote}>
                  {s.label}: {format(rows[hover]!.values[s.key] ?? 0)}
                </span>
              ))}
            {rows[hover]!.notes?.map((n) => (
              <span key={n} className={styles.tooltipNote}>
                {n}
              </span>
            ))}
          </Tooltip>
        ) : null}
        <table className="visually-hidden">
          <caption>{label}</caption>
          <thead>
            <tr>
              <th scope="col">Day</th>
              {series.map((s) => (
                <th key={s.key} scope="col">
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <th scope="row">{r.label}</th>
                {series.map((s) => (
                  <td key={s.key}>{format(r.values[s.key] ?? 0)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- lines over days (revenue)

export interface LineSeries {
  key: string;
  label: string;
  /** A colour token, e.g. var(--color-avatar-orbs-0-1); fixed per entity, never by rank. */
  color: string;
  values: number[];
  /** A faint fill under the line (use on one series at most). */
  area?: boolean;
}

/**
 * One or more series over the same days on one axis: 2px lines, an optional faint area, a recessive grid, a
 * crosshair with a dot per series and a tooltip listing every series, a legend with each series' latest value
 * (two series or more), and a visually hidden table with the same numbers.
 */
export function LineChart({
  labels,
  series,
  label,
  height = 180,
  format = (v) => fmt.format(v),
  integerTicks = true,
  testId = "line-chart",
}: {
  /** One axis label per point (e.g. "3 Oct"); every few are shown when they are dense. */
  labels: string[];
  series: LineSeries[];
  label: string;
  height?: number;
  format?: (v: number) => string;
  /** Counts: axis ticks on whole numbers. Turn off for money. */
  integerTicks?: boolean;
  testId?: string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const axisW = 44;
  const padT = 10;
  const padR = 6;
  const plotH = height - padT;
  const plotW = Math.max(40, width - axisW - padR);
  const n = labels.length;
  const max = niceMax(Math.max(0, ...series.flatMap((s) => s.values)), integerTicks);
  const x = (i: number) => axisW + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v: number) => padT + plotH - (Math.max(0, v) / max) * plotH;
  const every = Math.max(1, Math.ceil(n / Math.max(1, Math.floor(plotW / 64))));
  const quarter = [0.25, 0.5, 0.75, 1];
  const slot = n > 1 ? plotW / (n - 1) : plotW;
  const path = (values: number[]) => values.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const base = padT + plotH;
  return (
    <div className={styles.stackWrap}>
      {series.length > 1 ? (
        <ul className={styles.legend} aria-label={`${label}: legend`}>
          {series.map((s) => (
            <li key={s.key}>
              <span className={styles.swatch} style={{ background: s.color }} aria-hidden />
              <span>{s.label}</span>
              <span className={styles.legendValue}>{s.values.length ? format(s.values[s.values.length - 1]!) : "—"}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <div ref={ref} className={styles.chart} onMouseLeave={() => setHover(null)} data-testid={testId}>
        <svg width={width} height={height + 20} role="img" aria-label={label}>
          {quarter.map((q) => (
            <g key={q}>
              <line x1={axisW} x2={axisW + plotW} y1={y(q * max)} y2={y(q * max)} className={styles.grid} />
              <text x={axisW - 6} y={y(q * max) + 3} className={styles.tick} textAnchor="end">
                {format(q * max)}
              </text>
            </g>
          ))}
          <line x1={axisW} x2={axisW + plotW} y1={base} y2={base} className={styles.axis} />
          {series.map((s) =>
            s.area && n > 1 ? (
              <path key={`${s.key}-area`} d={`${path(s.values)} L${x(n - 1).toFixed(1)},${base} L${x(0).toFixed(1)},${base} Z`} className={styles.area} style={{ fill: s.color }} />
            ) : null,
          )}
          {series.map((s) => (
            <path key={s.key} d={path(s.values)} className={styles.line} style={{ stroke: s.color }} data-series={s.key} />
          ))}
          {labels.map((l, i) =>
            i % every === 0 ? (
              <text key={`${l}-${i}`} x={x(i)} y={base + 14} className={styles.tick} textAnchor="middle">
                {l}
              </text>
            ) : null,
          )}
          {hover !== null ? (
            <>
              <line x1={x(hover)} x2={x(hover)} y1={padT} y2={base} className={styles.crosshair} />
              {series.map((s) => (
                <circle key={s.key} cx={x(hover)} cy={y(s.values[hover] ?? 0)} r={4} className={styles.lineDot} style={{ fill: s.color }} />
              ))}
            </>
          ) : null}
          {labels.map((l, i) => (
            <rect key={`hit-${l}-${i}`} x={x(i) - slot / 2} y={padT} width={slot} height={plotH} fill="transparent" onMouseEnter={() => setHover(i)} />
          ))}
        </svg>
        {hover !== null && labels[hover] !== undefined ? (
          <Tooltip x={x(hover)} y={Math.min(...series.map((s) => y(s.values[hover] ?? 0))) - 8} width={width}>
            <span>{labels[hover]}</span>
            {series.map((s) => (
              <strong key={s.key}>
                {format(s.values[hover] ?? 0)} {series.length > 1 ? <span className={styles.tooltipNote}>{s.label}</span> : null}
              </strong>
            ))}
          </Tooltip>
        ) : null}
        <table className="visually-hidden">
          <caption>{label}</caption>
          <thead>
            <tr>
              <th scope="col">Day</th>
              {series.map((s) => (
                <th key={s.key} scope="col">
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {labels.map((l, i) => (
              <tr key={`${l}-${i}`}>
                <th scope="row">{l}</th>
                {series.map((s) => (
                  <td key={s.key}>{format(s.values[i] ?? 0)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
